/* global React, Card, Button, IconButton, Field, Input, Select, Modal, Badge, Icon, Checkbox, SettingsHead, toast, t */
// ============================================================
// Innstillinger -> Dokumenter
// ============================================================
// Foretakets egne dokumenter: avtaler, forsikringsbevis, vedtekter. Fila lastes
// til Firebase Storage og metadata lagres i app-state via PlummeBackend — ingen
// lagring i nettleseren, og ingen kopi av selve fila i klienten.
//
// Avkrysningene styrer hvilke utgaaende dokumenter dokumentet blir med paa.
// Salgsdokumentene legger det inn som et vedlegg brukeren kan fjerne for den
// enkelte sendingen; abonnement/lister legges paa av backend ved utsending.

// Navnet må være unikt paa tvers av HELE src: Plumme laster hver fil som
// klassisk skript, saa alle toppnivaa-`const` deler ett skop. `backend.jsx` har
// allerede en `companyDocumentsModel`, og et gjenbrukt navn her ville kastet
// «Identifier has already been declared» — hele fila stopper, og siden blir blank.
const settingsDocumentsModel = () => window.PlummeCompanyDocuments;

const companyDocumentTargetKeys = () => settingsDocumentsModel()?.TARGETS || [];
const companyDocumentTypeKeys = () => settingsDocumentsModel()?.DOCUMENT_TYPES || [];

const companyDocumentSize = (bytes) => {
  const size = Number(bytes || 0);
  if (!size) return '';
  const kb = size / 1024;
  return kb < 1024 ? `${Math.round(kb)} kB` : `${(kb / 1024).toFixed(1)} MB`;
};

const companyDocumentTargetLabels = (document) => companyDocumentTargetKeys()
  .filter((key) => document?.targets?.[key])
  .map((key) => t(`set.doc.target.${key}`));

function CompanyDocumentModal({ document, fileName, busy, onClose, onSave }) {
  const editing = !!document;
  const [form, setForm] = React.useState(() => ({
    name: document?.name || String(fileName || '').replace(/\.[^.]+$/, ''),
    documentType: document?.documentType || companyDocumentTypeKeys()[0] || 'other',
    targets: settingsDocumentsModel()?.normalizeTargets(document?.targets) || {},
  }));
  const set = (patch) => setForm((current) => ({ ...current, ...patch }));
  const setTarget = (key, value) => set({ targets: { ...form.targets, [key]: value } });
  const save = () => {
    const name = form.name.trim();
    if (!name) {
      toast(t('set.doc.nameRequired'), 'info');
      return;
    }
    onSave({ ...form, name });
  };
  return <Modal open onClose={busy ? () => {} : onClose} title={editing ? t('set.doc.edit') : t('set.doc.new')} max={520}
    footer={<React.Fragment>
      <Button variant="ghost" disabled={busy} onClick={onClose}>{t('common.cancel')}</Button>
      <Button icon="check" disabled={busy} onClick={save} data-help={t('helpTopics.documentLink.body')} data-help-title={t('helpTopics.documentLink.title')}>{busy ? t('set.doc.uploading') : (editing ? t('cust.form.save') : t('set.doc.add'))}</Button>
    </React.Fragment>}>
    <div className="col gap-14" style={{ paddingBottom: 4 }}>
      <Field label={t('set.doc.name')}><Input value={form.name} onChange={(event) => set({ name: event.target.value })} placeholder={t('set.doc.namePh')} /></Field>
      <Field label={t('set.doc.type')} help={t('helpTopics.documentLink.body')} helpTitle={t('helpTopics.documentLink.title')}>
        <Select value={form.documentType} onChange={(event) => set({ documentType: event.target.value })}>
          {companyDocumentTypeKeys().map((key) => <option key={key} value={key}>{t(`set.doc.kind.${key}`)}</option>)}
        </Select>
      </Field>
      <Field label={t('set.doc.attachTo')} hint={t('set.doc.attachToHint')} help={t('helpTopics.documentLink.body')} helpTitle={t('helpTopics.documentLink.title')}>
        <div className="col gap-8 mt-4">
          {companyDocumentTargetKeys().map((key) => <Checkbox key={key} checked={!!form.targets[key]} onChange={(value) => setTarget(key, value)}>
            {t(`set.doc.target.${key}`)}
          </Checkbox>)}
        </div>
      </Field>
      <p className="t-xs muted" style={{ margin: 0 }}>{t('set.doc.target.subscriptionHint')}</p>
    </div>
  </Modal>;
}

function SettingsDocumentsPage({ documents = [], canManage = true }) {
  const [drag, setDrag] = React.useState(false);
  const [pendingFile, setPendingFile] = React.useState(null);
  const [editing, setEditing] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [busyDocumentId, setBusyDocumentId] = React.useState('');
  const fileRef = React.useRef(null);

  const pickFile = (fileList) => {
    const files = Array.from(fileList || []).filter((file) => file instanceof File);
    if (!files.length || !canManage) return;
    setPendingFile(files[0]);
  };
  const clearFileInput = () => { if (fileRef.current) fileRef.current.value = ''; };

  const uploadDocument = async (form) => {
    setBusy(true);
    try {
      await window.PlummeBackend.uploadCompanyDocument(pendingFile, form);
      setPendingFile(null);
      clearFileInput();
      toast(t('set.doc.uploaded'), 'success');
    } catch (error) {
      toast(error?.message || t('set.doc.uploaded'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const saveDocument = async (form) => {
    setBusy(true);
    try {
      await window.PlummeBackend.saveCompanyDocument(editing.id, form);
      setEditing(null);
      toast(t('set.doc.saved'), 'success');
    } catch (error) {
      toast(error?.message || t('set.doc.saved'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const removeDocument = async (document) => {
    if (!window.confirm(t('set.doc.deleteConfirm', { name: document.name }))) return;
    setBusyDocumentId(document.id);
    try {
      await window.PlummeBackend.deleteCompanyDocument(document.id);
      toast(t('set.doc.deleted'), 'info');
    } catch (error) {
      toast(error?.message || t('set.doc.deleted'), 'error');
    } finally {
      setBusyDocumentId('');
    }
  };

  const openDocument = async (document) => {
    setBusyDocumentId(document.id);
    try {
      await window.PlummeBackend.openCompanyDocument(document);
    } catch (error) {
      toast(error?.message || t('svc.docFileNotFound'), 'error');
    } finally {
      setBusyDocumentId('');
    }
  };

  return <Card>
    <SettingsHead icon="files" title={t('set.doc.title')} sub={t('set.doc.sub')} />
    <label className={'dropzone' + (drag ? ' drag' : '')} style={{ padding: 28, cursor: canManage ? 'pointer' : 'not-allowed', opacity: canManage ? 1 : 0.6 }}
      onDragOver={(event) => { event.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(event) => { event.preventDefault(); setDrag(false); pickFile(event.dataTransfer.files); }}>
      <input ref={fileRef} type="file" style={{ display: 'none' }} disabled={!canManage}
        accept="application/pdf,image/jpeg,image/png,image/webp,.xlsx,.docx,.doc"
        onChange={(event) => { pickFile(event.target.files); clearFileInput(); }} />
      <Icon name="upload" size={26} />
      <b>{t('set.doc.drop')}</b>
      <span className="t-xs muted" style={{ fontWeight: 600 }}>{t('set.doc.dropHint')}</span>
    </label>
    <p className="t-xs muted" style={{ marginTop: 8 }}>{t('set.doc.fileHint')}</p>
    {documents.length > 0 ? <div className="col gap-8 mt-14">{documents.map((document) => <div key={document.id} className="company-doc-row">
      <span className="icon-chip" style={{ flex: 'none' }}><Icon name="fileText" size={17} /></span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <button type="button" className="company-doc-name" title={t('set.doc.open')} disabled={busyDocumentId === document.id} onClick={() => openDocument(document)}>{document.name}</button>
        <div className="t-xs muted" style={{ fontWeight: 600, marginTop: 2 }}>
          {t(`set.doc.kind.${document.documentType}`)}
          {document.uploadedAt ? ` · ${t('set.doc.added')} ${window.PlummeI18n.date(document.uploadedAt)}` : ''}
          {document.size ? ` · ${companyDocumentSize(document.size)}` : ''}
        </div>
        {companyDocumentTargetLabels(document).length > 0 && <div className="row gap-6 wrap" style={{ marginTop: 6 }}>
          {companyDocumentTargetLabels(document).map((label) => <Badge key={label} variant="neutral">{label}</Badge>)}
        </div>}
      </div>
      {canManage && <IconButton name="penBox" size="sm" variant="ghost" title={t('set.doc.edit')} disabled={busyDocumentId === document.id} onClick={() => setEditing(document)} />}
      {canManage && <IconButton name="trash" size="sm" variant="ghost" title={t('set.doc.remove')} disabled={busyDocumentId === document.id} onClick={() => removeDocument(document)}  data-help={t('helpTopics.documentLink.body')} data-help-title={t('helpTopics.documentLink.title')}/>}
    </div>)}</div> : <p className="t-sm muted" style={{ marginTop: 14 }}>{t('set.doc.empty')}</p>}
    {pendingFile && <CompanyDocumentModal fileName={pendingFile.name} busy={busy} onClose={() => { setPendingFile(null); clearFileInput(); }} onSave={uploadDocument} />}
    {editing && <CompanyDocumentModal document={editing} busy={busy} onClose={() => setEditing(null)} onSave={saveDocument} />}
  </Card>;
}

Object.assign(window, { SettingsDocumentsPage });
